import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { local } from "../lib/storage.ts";
import { en } from "./en.ts";
import { fr } from "./fr.ts";

export const LOCALES = ["en", "fr"] as const;
export type Locale = (typeof LOCALES)[number];
export const LANG_STORAGE_KEY = "esi.lang";

declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "translation";
    resources: { translation: typeof en };
  }
}

const isLocale = (value: unknown): value is Locale => LOCALES.includes(value as Locale);

/** The stored choice, else the browser's first French or English preference, else English. */
export function detectLocale(): Locale {
  const stored = local.get(LANG_STORAGE_KEY);
  if (isLocale(stored)) {
    return stored;
  }
  const preferred = typeof navigator === "undefined" ? [] : (navigator.languages ?? []);
  for (const tag of preferred) {
    const language = tag.toLowerCase().split("-")[0];
    if (isLocale(language)) {
      return language;
    }
  }
  return "en";
}

/**
 * French plurals have a `many` category (1 000 000) that i18next does not fall back from:
 * copy each `_other` key to `_many` so every catalogue only writes `_one` and `_other`.
 */
function withMany<T>(catalogue: T): T {
  if (typeof catalogue !== "object" || catalogue === null) {
    return catalogue;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(catalogue)) {
    out[key] = withMany(value);
    if (key.endsWith("_other")) {
      out[`${key.slice(0, -"_other".length)}_many`] = value;
    }
  }
  return out as T;
}

/** One shared instance: components read it through `useTranslation`, helpers through `t`. */
export const i18n = i18next.createInstance();

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, fr: { translation: withMany(fr) } },
  lng: detectLocale(),
  fallbackLng: false,
  supportedLngs: LOCALES,
  interpolation: { escapeValue: false },
  returnNull: false,
  initAsync: false,
});

const syncLang = (lng: string) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
};
syncLang(i18n.language);
i18n.on("languageChanged", syncLang);

export const t = i18n.t.bind(i18n);

export function currentLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : "en";
}

/** Switches the language without a reload and remembers it in this browser. */
export async function setLocale(locale: Locale): Promise<void> {
  local.set(LANG_STORAGE_KEY, locale);
  await i18n.changeLanguage(locale);
}
